import { FC, useMemo } from 'react';
import { TooltipProps } from 'recharts';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { PunchupStackedBarChart } from 'features/common/components/Punchup/primitives/charts/PunchupStackedBarChart';
import { PunchupChartTooltip } from 'features/common/components/Punchup/primitives/charts/PunchupChartTooltip';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { TimelineControls } from 'features/PunchupPages/Analytics/TimelineControls';
import { TICKETS_SOLD } from 'features/common/hooks/useChart/utils';
import { CHART_SERIES_COLORS } from 'features/common/utils/chartColors';
import { formatCompactCurrency } from 'features/common/utils/chartFormatters';
import { customXTick, toUsFormat } from 'utils/utils';
import { useChart } from 'features/common/hooks/useChart/useChart';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { ChartPalette } from '../PunchupAnalytics';

interface Props {
	ticketTypeColors: ChartPalette['ticketTypeColors'];
}

const formatTooltipDate = (label: unknown): string => moment(label as moment.MomentInput).format('MM/DD/YY');

const NetSalesByTypeTooltip: FC<TooltipProps<number, string>> = (props) => (
	<PunchupChartTooltip {...props} formatHeader={formatTooltipDate} formatValue={toUsFormat} />
);

const PunchupNetSalesByTypeSection: FC<Props> = ({ ticketTypeColors }) => {
	const { t } = useTranslation();
	const { event } = useGetEvent();
	const {
		ticketTypeDataFrame,
		dataKeys,
		setDisplay,
		displayWindow,
		isLoading,
		minDate,
		maxDate,
		windowStartDate,
		windowEndDate,
		setDateRange,
	} = useChart(TICKETS_SOLD);

	// Build effective price per ticket type name.
	// PWYW: use midpoint of floor and max price (consistent with inventory section estimates).
	// Free: price = 0 → excluded from bars to avoid confusing zero-height stacks.
	const priceByName = useMemo<Record<string, number>>(() => {
		const map: Record<string, number> = {};
		for (const tt of event?.ticketTypes ?? []) {
			if (tt.payWhatYouWant) {
				const maxP = tt.maxPrice ?? 0;
				map[tt.name] = (tt.initialPrice + maxP) / 2;
			} else {
				map[tt.name] = tt.initialPrice;
			}
		}
		return map;
	}, [event?.ticketTypes]);

	// Revenue = tickets issued × effective price per type per period bucket.
	const revenueDataFrame = useMemo(() => {
		if (!ticketTypeDataFrame) return [];
		return ticketTypeDataFrame.map((entry) => {
			const result: Record<string, unknown> = { date: (entry as Record<string, unknown>).date };
			for (const key of dataKeys ?? []) {
				const count = ((entry as Record<string, unknown>)[key] as number) ?? 0;
				result[key] = count * (priceByName[key] ?? 0);
			}
			return result;
		});
	}, [ticketTypeDataFrame, dataKeys, priceByName]);

	// Include only types with a non-zero price; free types contribute $0 to every bar.
	// Memoised so its reference is stable — it feeds both the totalNetSales useMemo dep array
	// and the React.memo'd PunchupStackedBarChart, neither of which can cache against a fresh
	// array each render.
	const activeBars = useMemo(
		() =>
			(dataKeys ?? [])
				.filter((key) => (priceByName[key] ?? 0) > 0)
				.map((key, i) => ({
					dataKey: key,
					color: ticketTypeColors[key] ?? CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length],
					name: key,
				})),
		[dataKeys, priceByName, ticketTypeColors]
	);

	const totalNetSales = useMemo(() => {
		return revenueDataFrame.reduce((sum, entry) => {
			return (
				sum +
				activeBars.reduce((typeSum, bar) => {
					return typeSum + (((entry as Record<string, unknown>)[bar.dataKey] as number) ?? 0);
				}, 0)
			);
		}, 0);
	}, [revenueDataFrame, activeBars]);

	const hasPwyw = (dataKeys ?? []).some((key) => event?.ticketTypes.find((tt) => tt.name === key)?.payWhatYouWant);

	return (
		<PunchupStackedBarChart
			isLoading={isLoading}
			data={revenueDataFrame as Record<string, unknown>[]}
			bars={activeBars}
			title={t('analytics-revenue-by-type')}
			totalValue={hasPwyw ? `${toUsFormat(totalNetSales)}*` : toUsFormat(totalNetSales)}
			xAxisFormatter={(v) => customXTick(v as Date)}
			yAxisFormatter={formatCompactCurrency}
			headerRight={
				<TimelineControls
					setDisplay={setDisplay}
					displayWindow={displayWindow}
					minDate={minDate}
					maxDate={maxDate}
					windowStartDate={windowStartDate}
					windowEndDate={windowEndDate}
					setDateRange={setDateRange}
				/>
			}
			customTooltip={NetSalesByTypeTooltip}
			helpButton={
				<PunchupHelpButton
					mainText="A price × tickets estimate per ticket type for the selected period — not confirmed payout. The Revenue tile up top is the actual money received."
					codeText={
						'PWYW types use midpoint (floor + max price) / 2 — marked with *\nFree types are not shown (zero revenue)'
					}
					secondaryText="Compare to Tickets Sold to see the volume-to-revenue relationship. A high-volume low-priced tier may contribute less dollar value than a small VIP tier."
				/>
			}
		/>
	);
};

export default PunchupNetSalesByTypeSection;
